import axiosInstance from './axiosInstance';

const businessService = {
    // Get all businesses
    async getAllBusinesses(agentId = null) {
        try {
            const url = agentId ? `/businesses?agentId=${agentId}` : '/businesses';
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Create new business registration
    async registerBusiness(businessData) {
        try {
            const response = await axiosInstance.post('/businesses', businessData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Get business by ID
    async getBusiness(id) {
        try {
            const response = await axiosInstance.get(`/businesses/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Update business registration
    async updateBusiness(id, businessData) {
        try {
            const response = await axiosInstance.put(`/businesses/${id}`, businessData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Save draft business registration
    async saveDraft(businessData) {
        try {
            const response = await axiosInstance.post('/businesses', {
                ...businessData,
                status: 'draft'
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Submit business registration
    async submitBusiness(id, businessData) {
        try {
            if (!id) {
                throw new Error('Business ID is required for submission');
            }
            const response = await axiosInstance.post(`/business/${id}/submit`, businessData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Calculate total price
    async calculatePrice(priceData) {
        try {
            const response = await axiosInstance.post('/business/calculate-price', priceData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    async updateBusinessPackage(businessId, packageType) {
        try {
            const response = await axiosInstance.post(`/businesses/${businessId}/package`, {
                businessId,
                packageType
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to update business package');
        }
    },

    // Assign a registered agent to businesses
    async assignRegisteredAgent({ businessIds, agentId }) {
        try {
            const response = await axiosInstance.post(`/businesses/manage/assign-agent`, {
                businessIds,
                agentId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Create owner for a business
    async createOwner(businessId, ownerData) {
        try {
            const response = await axiosInstance.post(`/businesses/${businessId}/owner`, ownerData);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error creating owner:', error);
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to create owner'
            };
        }
    },

    // Create address for a business
    async createAddress(businessId, addressData) {
        try {
            const response = await axiosInstance.post(`/businesses/${businessId}/address`, addressData);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Error creating address:', error);
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to create address'
            };
        }
    },

    async addUserToBusiness(businessId, userData) {
        try {
            console.log('Adding user to business:', { businessId, userData });            // Step 1: Create customer account
            const createCustomerResponse = await axiosInstance.post('/customers', {
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                phone: userData.phone,
                password: '123456', // Default password for all new users
                status: 'Active',
                role: userData.role
            });

            console.log('Customer created:', createCustomerResponse.data);

            // Check if the response has the expected structure
            if (!createCustomerResponse.data || !createCustomerResponse.data.success || !createCustomerResponse.data.data) {
                throw new Error('Invalid response format from customer creation');
            }

            const customerId = createCustomerResponse.data.data.id;
            if (!customerId) {
                throw new Error('Customer ID not found in response');
            }

            // Ensure we have the customer token
            const customerToken = localStorage.getItem('customerToken');
            if (!customerToken) {
                throw new Error('Customer authentication token not found');
            }

            // Step 2: Link customer to business
            const linkResponse = await axiosInstance.post('/customer-business', {
                businessId,
                customerId,
                role: userData.role
            });

            console.log('Customer linked to business:', linkResponse.data);
            return linkResponse.data;
        } catch (error) {
            console.error('Error adding user to business:', error);
            throw error.response?.data || error.message;
        }
    },

    async removeUserFromBusiness(businessId, userId) {
        try {
            console.log('Removing user from business:', { businessId, userId });
            const response = await axiosInstance.delete(`/customer-business/${userId}/${businessId}`);
            console.log('Remove user response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error removing user from business:', error);
            throw error.response?.data || error.message;
        }
    },    async updateUserStatus(userId, status) {
        try {
            console.log('Updating user status:', { userId, status });
            const response = await axiosInstance.put(`/customers/${userId}/status`, {
                status
            });
            console.log('Update user status response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error updating user status:', error);
            throw error.response?.data || error.message;
        }
    },
      async updateUserDetails(userId, userData) {
        try {
            console.log('Updating user details:', { userId, userData });
            // Use the customer-business endpoint instead of directly updating the customer
            const response = await axiosInstance.put(`/customer-business/update-user/${userId}`, userData);
            console.log('Update user details response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error updating user details:', error);
            throw error.response?.data || error.message;
        }
    },

    async getBusinessUsers(businessId) {
        try {
            console.log('Fetching users for business:', businessId);
            if (!businessId) {
                throw new Error('Business ID is required');
            }

            // Ensure we have the customer token
            const customerToken = localStorage.getItem('customerToken');
            console.log('Customer token exists:', !!customerToken);

            if (!customerToken) {
                throw new Error('Customer authentication token not found');
            }

            console.log('Making API request to:', `/customer-business/business/${businessId}`);
            const response = await axiosInstance.get(`/customer-business/business/${businessId}`);
            console.log('Raw API response:', response);

            if (!response || !response.data) {
                console.error('No response or response data received');
                throw new Error('No response received from server');
            }

            console.log('Response data:', response.data);

            // Handle different response formats
            if (response.data.success && Array.isArray(response.data.data)) {
                console.log('Processing successful response with data array');
                return response.data;
            } else if (Array.isArray(response.data)) {
                console.log('Processing direct array response');
                return {
                    success: true,
                    data: response.data
                };
            } else if (response.data.data && Array.isArray(response.data.data)) {
                console.log('Processing response with nested data array');
                return response.data;
            } else {
                console.error('Invalid response format:', response.data);
                throw new Error('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error fetching business users:', error);
            if (error.response) {
                console.error('Error response:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }
            if (error.response?.status === 401) {
                throw new Error('Authentication failed. Please log in again.');
            } else if (error.response?.status === 404) {
                throw new Error('Business not found.');
            } else {
                throw error.response?.data || error.message;
            }
        }
    }
};

export default businessService;